import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useApplicationUsageReport } from '../../hooks/useApplicationUsageReport';
import { useFoundInLibraryReport, FIL_PERIOD_OPTIONS, FILPeriod } from '../../hooks/useFoundInLibraryReport';
import { useDownloadAttachment } from '../../App.hooks';
import { ticksToDate } from '../../Utils';
import RefreshButton from '../../components/main/RefreshButton/RefreshButton';
import { TrendingUnapprovedContentSummaryReport, UnapprovedContentSummaryReport } from './UnapprovedContentSummaryReports';
import { Box, FolderKanban, Loader, AlertCircle, BarChart3, Monitor, ArrowLeft, ChevronRight, FileWarning, Users, ChevronDown, ChevronUp, Sparkles, Play, X, CheckCircle2, Library } from 'lucide-react';

type ReportsProps = {
    style?: CSSProperties | undefined;
};

const MONTHS_OPTIONS = [3, 6, 12, 24];

type ReportDefinition = {
    id: string;
    title: string;
    description: string;
    icon: typeof Monitor;
    iconColor: string;
    iconBg: string;
};

const REPORT_DEFINITIONS: ReportDefinition[] = [
    {
        id: 'application-usage',
        title: 'Application Usage Report',
        description: 'Revit & application version usage across projects and models',
        icon: Monitor,
        iconColor: 'var(--warning)',
        iconBg: 'var(--warning-soft)',
    },
    {
        id: 'unapproved-content',
        title: 'Unapproved Content',
        description: 'Enterprise rollup of unapproved content across all projects',
        icon: FileWarning,
        iconColor: 'var(--danger)',
        iconBg: 'var(--danger-soft, #fef2f2)',
    },
    {
        id: 'trending-unapproved',
        title: 'Trending Unapproved Content',
        description: 'Family-level frequency analysis from daily summaries',
        icon: Sparkles,
        iconColor: 'var(--accent)',
        iconBg: 'var(--accent-soft)',
    },
    {
        id: 'found-in-library',
        title: 'Found in Library',
        description: 'Enterprise % of content found in library, broken down by client',
        icon: Library,
        iconColor: 'var(--success)',
        iconBg: 'var(--success-soft)',
    },
];

const Reports = (props: ReportsProps) => {
    const { style } = props;
    const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

    if (selectedReportId === 'application-usage') {
        return <ApplicationUsageReport style={style} onBack={() => setSelectedReportId(null)} />;
    }
    if (selectedReportId === 'unapproved-content') {
        return <UnapprovedContentSummaryReport style={style} onBack={() => setSelectedReportId(null)} />;
    }
    if (selectedReportId === 'trending-unapproved') {
        return <TrendingUnapprovedContentSummaryReport style={style} onBack={() => setSelectedReportId(null)} />;
    }
    if (selectedReportId === 'found-in-library') {
        return <FoundInLibraryReport style={style} onBack={() => setSelectedReportId(null)} />;
    }

    return (
        <div className="page-container" style={{ ...style, overflow: 'auto' }} data-testid="Reports">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Reports</h1>
                    <p className="page-subtitle">Select a report to view detailed insights</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-slide-up">
                {REPORT_DEFINITIONS.map((report) => {
                    const Icon = report.icon;
                    return (
                        <button
                            key={report.id}
                            onClick={() => setSelectedReportId(report.id)}
                            className="card p-5 text-left transition-all"
                            style={{
                                border: '1px solid var(--border-default)',
                                cursor: 'pointer',
                                background: 'var(--bg-card)',
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.borderColor = 'var(--accent)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.borderColor = 'var(--border-default)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: report.iconBg }}
                                >
                                    <Icon size={20} style={{ color: report.iconColor }} />
                                </div>
                                <ChevronRight size={18} style={{ color: 'var(--text-tertiary)' }} />
                            </div>
                            <h3 className="text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                                {report.title}
                            </h3>
                            <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                {report.description}
                            </p>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

type ApplicationUsageReportProps = {
    style?: CSSProperties | undefined;
    onBack: () => void;
};

const ApplicationUsageReport = ({ style, onBack }: ApplicationUsageReportProps) => {
    const [monthsBack, setMonthsBack] = useState(12);
    const { fetch, report, loading, error, fetchedCount } = useApplicationUsageReport({ monthsBack });

    useEffect(() => {
        fetch();
    }, [monthsBack]);

    /** Largest model count across rows, used to scale the bar chart. */
    const maxModelCount = useMemo(() => {
        if (!report?.rows.length) return 1;
        return Math.max(...report.rows.map((r) => r.modelCount));
    }, [report]);

    return (
        <div className="page-container" style={{ ...style, overflow: 'auto' }} data-testid="ApplicationUsageReport">
            {/* Header */}
            <div className="page-header">
                <div>
                    <button
                        onClick={onBack}
                        className="flex items-center gap-1 text-xs font-medium mb-2 transition-colors"
                        style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        onMouseOver={(e) => { e.currentTarget.style.color = 'var(--accent)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
                    >
                        <ArrowLeft size={14} /> Back to Reports
                    </button>
                    <h1 className="page-title">Application Usage Report</h1>
                    <p className="page-subtitle">
                        Revit &amp; application version usage across projects and models
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Time range selector */}
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>
                            Period:
                        </span>
                        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-default)' }}>
                            {MONTHS_OPTIONS.map((m) => (
                                <button
                                    key={m}
                                    onClick={() => setMonthsBack(m)}
                                    className="px-3 py-1.5 text-xs font-medium transition-colors"
                                    style={{
                                        backgroundColor: monthsBack === m ? 'var(--accent)' : 'var(--bg-card)',
                                        color: monthsBack === m ? '#fff' : 'var(--text-secondary)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        outline: 'none',
                                    }}
                                >
                                    {m}mo
                                </button>
                            ))}
                        </div>
                    </div>
                    <RefreshButton onRefresh={fetch} className="btn-secondary" iconSize={16} />
                </div>
            </div>

            {/* Loading State */}
            {loading && (
                <div className="card p-8 flex flex-col items-center justify-center gap-3 animate-slide-up">
                    <Loader size={28} className="animate-spin" style={{ color: 'var(--accent)' }} />
                    <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        Fetching model data…
                    </div>
                    <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                        {fetchedCount.toLocaleString()} models loaded so far
                    </div>
                </div>
            )}

            {/* Error State */}
            {error && !loading && (
                <div
                    className="card p-6 flex items-center gap-3"
                    style={{ borderLeft: '4px solid var(--danger)' }}
                >
                    <AlertCircle size={20} style={{ color: 'var(--danger)' }} />
                    <div>
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            Failed to load report
                        </div>
                        <div className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>{error}</div>
                    </div>
                </div>
            )}

            {/* Report Content */}
            {report && !loading && (
                <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-slide-up">
                        {/* Total Models */}
                        <div className="card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: 'var(--info-soft)' }}
                                >
                                    <Box size={20} style={{ color: 'var(--info)' }} />
                                </div>
                                <span
                                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                    style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-tertiary)' }}
                                >
                                    last {monthsBack}mo
                                </span>
                            </div>
                            <div className="stat-value">{report.totalModels.toLocaleString()}</div>
                            <div className="stat-label mt-1">Models Updated</div>
                            <div className="text-xs mt-2" style={{ color: 'var(--text-tertiary)' }}>
                                of {report.totalFetched.toLocaleString()} total models
                            </div>
                        </div>

                        {/* Total Projects */}
                        <div className="card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: 'var(--success-soft)' }}
                                >
                                    <FolderKanban size={20} style={{ color: 'var(--success)' }} />
                                </div>
                                <span
                                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                    style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-tertiary)' }}
                                >
                                    last {monthsBack}mo
                                </span>
                            </div>
                            <div className="stat-value">{report.totalProjects.toLocaleString()}</div>
                            <div className="stat-label mt-1">Projects With Models</div>
                        </div>

                        {/* Application Count */}
                        <div className="card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div
                                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: 'var(--warning-soft)' }}
                                >
                                    <Monitor size={20} style={{ color: 'var(--warning)' }} />
                                </div>
                                <span
                                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                    style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-tertiary)' }}
                                >
                                    distinct
                                </span>
                            </div>
                            <div className="stat-value">{report.rows.length.toLocaleString()}</div>
                            <div className="stat-label mt-1">Applications Detected</div>
                        </div>
                    </div>

                    {/* Bar Chart Visualization */}
                    <div className="card p-6 animate-slide-up">
                        <div className="flex items-center gap-2 mb-5">
                            <BarChart3 size={18} style={{ color: 'var(--text-tertiary)' }} />
                            <h2 className="section-title" style={{ margin: 0 }}>Usage by Application</h2>
                        </div>

                        {report.rows.length === 0 ? (
                            <div className="text-sm text-center py-8" style={{ color: 'var(--text-tertiary)' }}>
                                No model activity found in the selected period.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {report.rows.map((row) => {
                                    const pct = (row.modelCount / maxModelCount) * 100;
                                    return (
                                        <div key={row.applicationName} className="group">
                                            <div className="flex items-center justify-between mb-1">
                                                <span
                                                    className="text-sm font-medium truncate"
                                                    style={{ color: 'var(--text-primary)' }}
                                                    title={row.applicationName}
                                                >
                                                    {row.applicationName}
                                                </span>
                                                <div className="flex items-center gap-4 text-xs flex-shrink-0 ml-4">
                                                    <span style={{ color: 'var(--info)' }}>
                                                        <Box size={12} className="inline mr-1" style={{ verticalAlign: '-2px' }} />
                                                        {row.modelCount.toLocaleString()} models
                                                    </span>
                                                    <span style={{ color: 'var(--success)' }}>
                                                        <FolderKanban size={12} className="inline mr-1" style={{ verticalAlign: '-2px' }} />
                                                        {row.projectCount.toLocaleString()} projects
                                                    </span>
                                                </div>
                                            </div>
                                            <div
                                                className="w-full h-6 rounded-md overflow-hidden"
                                                style={{ backgroundColor: 'var(--bg-secondary)' }}
                                            >
                                                <div
                                                    className="h-full rounded-md transition-all duration-500 ease-out"
                                                    style={{
                                                        width: `${pct}%`,
                                                        background: 'linear-gradient(90deg, var(--accent), var(--accent-hover))',
                                                        minWidth: pct > 0 ? '4px' : '0',
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Data Table */}
                    <div className="card animate-slide-up">
                        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-default)' }}>
                            <h2 className="section-title" style={{ margin: 0 }}>Detailed Breakdown</h2>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr
                                        className="text-left text-xs font-semibold uppercase tracking-wider"
                                        style={{
                                            color: 'var(--text-tertiary)',
                                            backgroundColor: 'var(--bg-secondary)',
                                        }}
                                    >
                                        <th className="px-6 py-3">Application</th>
                                        <th className="px-6 py-3 text-right">Models</th>
                                        <th className="px-6 py-3 text-right">Projects</th>
                                        <th className="px-6 py-3 text-right">% of Models</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.rows.map((row, idx) => (
                                        <tr
                                            key={row.applicationName}
                                            className="transition-colors"
                                            style={{
                                                borderBottom: '1px solid var(--border-default)',
                                                backgroundColor: idx % 2 === 0 ? 'transparent' : 'var(--bg-secondary)',
                                            }}
                                            onMouseOver={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                                            }}
                                            onMouseOut={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                    idx % 2 === 0 ? 'transparent' : 'var(--bg-secondary)';
                                            }}
                                        >
                                            <td
                                                className="px-6 py-3 text-sm font-medium"
                                                style={{ color: 'var(--text-primary)' }}
                                            >
                                                {row.applicationName}
                                            </td>
                                            <td
                                                className="px-6 py-3 text-sm text-right tabular-nums"
                                                style={{ color: 'var(--text-secondary)' }}
                                            >
                                                {row.modelCount.toLocaleString()}
                                            </td>
                                            <td
                                                className="px-6 py-3 text-sm text-right tabular-nums"
                                                style={{ color: 'var(--text-secondary)' }}
                                            >
                                                {row.projectCount.toLocaleString()}
                                            </td>
                                            <td
                                                className="px-6 py-3 text-sm text-right tabular-nums"
                                                style={{ color: 'var(--text-secondary)' }}
                                            >
                                                {report.totalModels > 0
                                                    ? ((row.modelCount / report.totalModels) * 100).toFixed(1)
                                                    : '0.0'}
                                                %
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                {report.rows.length > 1 && (
                                    <tfoot>
                                        <tr
                                            className="font-semibold text-sm"
                                            style={{
                                                borderTop: '2px solid var(--border-default)',
                                                backgroundColor: 'var(--bg-secondary)',
                                                color: 'var(--text-primary)',
                                            }}
                                        >
                                            <td className="px-6 py-3">Total</td>
                                            <td className="px-6 py-3 text-right tabular-nums">
                                                {report.totalModels.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-3 text-right tabular-nums">
                                                {report.totalProjects.toLocaleString()}
                                            </td>
                                            <td className="px-6 py-3 text-right tabular-nums">100.0%</td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Unapproved Content (Enterprise) Report                             */
/* ------------------------------------------------------------------ */

const GET_PROJECTS_REPORT_DYNAMIC_DATA = gql`
    query getProjectsReportDynamicData {
        ProjectList(first: 10000) {
            nodes {
                id
                name
                dynamicData {
                    id
                    name
                    type
                    data
                    createdTime
                    createdById
                    createdBy {
                        id
                        name
                    }
                }
            }
        }
    }
`;

const PERIOD_OPTIONS: { id: 'week' | 'month' | 'quarter'; label: string; days: number }[] = [
    { id: 'week', label: '1 week', days: 7 },
    { id: 'month', label: '1 month', days: 30 },
    { id: 'quarter', label: '3 months', days: 90 },
];

type GroupBy = 'project' | 'user';

type UnapprovedItem = {
    rowId: string;
    projectId: string;
    projectName: string;
    reportId: string;
    reportCreatedTime: string | null;
    reportCreatedBy: string;
    modelName: string;
    familyName: string;
};

type GroupDetail = {
    loading: boolean;
    error: string | null;
    items: UnapprovedItem[];
    downloaded: number;
    failed: number;
    total: number;
};

type UnapprovedContentReportProps = {
    style?: CSSProperties | undefined;
    onBack: () => void;
};

// Kept temporarily as a rollback path while the summary-backed report settles.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const UnapprovedContentReport = ({ style, onBack }: UnapprovedContentReportProps) => {
    const [periodId, setPeriodId] = useState<'week' | 'month' | 'quarter'>('month');
    const [groupBy, setGroupBy] = useState<GroupBy>('project');
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [groupDetails, setGroupDetails] = useState<Map<string, GroupDetail>>(new Map());
    const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
    const download = useDownloadAttachment();

    const period = PERIOD_OPTIONS.find((p) => p.id === periodId)!;
    const cutoffSeconds = useMemo(() => {
        // createdTime is Unix seconds since 1970-01-01 (despite GraphQL field naming)
        const cutoffMs = Date.now() - period.days * 24 * 60 * 60 * 1000;
        return Math.floor(cutoffMs / 1000);
    }, [period]);

    const { data, loading, refetch } = useQuery(GET_PROJECTS_REPORT_DYNAMIC_DATA);

    // Diagnostic counts at each filtering stage
    const diagnostics = useMemo(() => {
        const projects: any[] = data?.ProjectList?.nodes || [];
        let projectsWithAnyDynamicData = 0;
        let totalDynamicData = 0;
        let totalReportType = 0;
        const distinctReportNames = new Set<string>();
        let totalUnapprovedAnyTime = 0;
        for (const project of projects) {
            const dd: any[] = project?.dynamicData || [];
            if (dd.length) projectsWithAnyDynamicData++;
            totalDynamicData += dd.length;
            for (const d of dd) {
                if (d?.type === 'Report') {
                    totalReportType++;
                    if (d?.name) distinctReportNames.add(d.name);
                    if ((d?.name || '').toLowerCase().trim() === 'unapproved content') {
                        totalUnapprovedAnyTime++;
                    }
                }
            }
        }
        return {
            totalProjects: projects.length,
            projectsWithAnyDynamicData,
            totalDynamicData,
            totalReportType,
            distinctReportNames: Array.from(distinctReportNames),
            totalUnapprovedAnyTime,
        };
    }, [data]);

    // Filter to Unapproved Content reports within the selected period (metadata only)
    const eligibleReports = useMemo(() => {
        const projects: any[] = data?.ProjectList?.nodes || [];
        const result: { projectId: string; projectName: string; report: any }[] = [];
        for (const project of projects) {
            if (!project?.id) continue;
            const reports = (project.dynamicData || []).filter((d: any) =>
                d?.type === 'Report' &&
                (d?.name || '').toLowerCase().trim() === 'unapproved content' &&
                d?.data &&
                d?.createdTime &&
                Number(d.createdTime) >= cutoffSeconds
            );
            for (const report of reports) {
                result.push({ projectId: project.id, projectName: project.name ?? '(unnamed project)', report });
            }
        }
        return result;
    }, [data, cutoffSeconds]);

    // Build groups from REPORT METADATA only (no downloads). Count = number of
    // report snapshots in the period. Detail items are fetched lazily on expand.
    const groups = useMemo(() => {
        const map = new Map<string, { key: string; label: string; reports: typeof eligibleReports }>();
        for (const entry of eligibleReports) {
            let key: string;
            let label: string;
            if (groupBy === 'project') {
                key = entry.projectId;
                label = entry.projectName;
            } else {
                key = entry.report.createdById || 'unknown';
                label = entry.report.createdBy?.name || entry.report.createdById || '(unknown user)';
            }
            const existing = map.get(key);
            if (existing) existing.reports.push(entry);
            else map.set(key, { key, label, reports: [entry] });
        }
        const arr = Array.from(map.values());
        arr.sort((a, b) => sortDir === 'desc' ? b.reports.length - a.reports.length : a.reports.length - b.reports.length);
        return arr;
    }, [eligibleReports, groupBy, sortDir]);

    const totals = useMemo(() => {
        const projects = new Set(eligibleReports.map((e) => e.projectId));
        const users = new Set(eligibleReports.map((e) => e.report.createdById || e.report.createdBy?.name || 'unknown'));
        return {
            totalReports: eligibleReports.length,
            totalProjects: projects.size,
            totalUsers: users.size,
        };
    }, [eligibleReports]);

    // Reset expanded groups & cached detail when grouping or period changes
    useEffect(() => {
        setExpandedGroups(new Set());
        setGroupDetails(new Map());
    }, [groupBy, periodId]);

    // Lazy-download a group's detail when it's first expanded
    const fetchGroupDetail = useCallback(async (groupKey: string, reports: typeof eligibleReports) => {
        setGroupDetails((prev) => {
            const next = new Map(prev);
            next.set(groupKey, { loading: true, error: null, items: [], downloaded: 0, failed: 0, total: reports.length });
            return next;
        });

        const CONCURRENCY = 6;
        const MAX_RETRIES = 2;
        const results: UnapprovedItem[][] = new Array(reports.length);
        let completed = 0;
        let failed = 0;

        const fetchOne = async (idx: number) => {
            const { projectId, projectName, report } = reports[idx];
            for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
                try {
                    const raw = await download(report.data);
                    let parsed: any;
                    if (typeof raw === 'string') {
                        try { parsed = JSON.parse(raw); }
                        catch { parsed = JSON.parse(atob(raw)); }
                    } else {
                        parsed = raw;
                    }
                    const list: any[] = Array.isArray(parsed) ? parsed : [parsed];
                    results[idx] = list.map<UnapprovedItem>((item, i) => ({
                        rowId: `${report.id}-${i}`,
                        projectId,
                        projectName,
                        reportId: report.id,
                        reportCreatedTime: report.createdTime ?? null,
                        reportCreatedBy: report.createdBy?.name || report.createdById || '(unknown user)',
                        modelName: item.ModelName ?? item.CmsModelName ?? '',
                        familyName: item.FamilyName ?? '',
                    }));
                    return;
                } catch {
                    if (attempt < MAX_RETRIES) {
                        await new Promise((r) => setTimeout(r, 250 * Math.pow(3, attempt)));
                    }
                }
            }
            results[idx] = [];
            failed++;
        };

        let nextIndex = 0;
        const worker = async () => {
            while (true) {
                const i = nextIndex++;
                if (i >= reports.length) return;
                await fetchOne(i);
                completed++;
                setGroupDetails((prev) => {
                    const next = new Map(prev);
                    const cur = next.get(groupKey);
                    if (cur) next.set(groupKey, { ...cur, downloaded: completed, failed });
                    return next;
                });
            }
        };

        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, reports.length) }, () => worker()));

        setGroupDetails((prev) => {
            const next = new Map(prev);
            next.set(groupKey, {
                loading: false,
                error: null,
                items: results.flat(),
                downloaded: completed,
                failed,
                total: reports.length,
            });
            return next;
        });
    }, [download]);

    const toggleGroup = (key: string, reports: typeof eligibleReports) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
                // Fire-and-forget lazy fetch the first time this group is expanded
                if (!groupDetails.has(key)) {
                    void fetchGroupDetail(key, reports);
                }
            }
            return next;
        });
    };

    const isLoadingAny = loading;

    return (
        <div className="page-container" style={{ ...style, overflow: 'auto' }} data-testid="UnapprovedContentReport">
            {/* Header */}
            <div className="page-header">
                <div>
                    <button
                        onClick={onBack}
                        className="flex items-center gap-1 text-xs font-medium mb-2 transition-colors"
                        style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        onMouseOver={(e) => { e.currentTarget.style.color = 'var(--accent)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
                    >
                        <ArrowLeft size={14} /> Back to Reports
                    </button>
                    <h1 className="page-title">Unapproved Content</h1>
                    <p className="page-subtitle">
                        Enterprise rollup of unapproved content across all projects
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Period selector */}
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>Period:</span>
                        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-default)' }}>
                            {PERIOD_OPTIONS.map((p) => (
                                <button
                                    key={p.id}
                                    onClick={() => setPeriodId(p.id)}
                                    className="px-3 py-1.5 text-xs font-medium transition-colors"
                                    style={{
                                        backgroundColor: periodId === p.id ? 'var(--accent)' : 'var(--bg-card)',
                                        color: periodId === p.id ? '#fff' : 'var(--text-secondary)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        outline: 'none',
                                    }}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* Group-by selector */}
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>Group by:</span>
                        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-default)' }}>
                            {(['project', 'user'] as GroupBy[]).map((g) => (
                                <button
                                    key={g}
                                    onClick={() => setGroupBy(g)}
                                    className="px-3 py-1.5 text-xs font-medium transition-colors capitalize"
                                    style={{
                                        backgroundColor: groupBy === g ? 'var(--accent)' : 'var(--bg-card)',
                                        color: groupBy === g ? '#fff' : 'var(--text-secondary)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        outline: 'none',
                                    }}
                                >
                                    {g}
                                </button>
                            ))}
                        </div>
                    </div>
                    <RefreshButton onRefresh={refetch} className="btn-secondary" iconSize={16} />
                </div>
            </div>

            {/* Loading */}
            {isLoadingAny && (
                <div className="card p-8 flex flex-col items-center justify-center gap-3 animate-slide-up">
                    <Loader size={28} className="animate-spin" style={{ color: 'var(--accent)' }} />
                    <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        Loading projects…
                    </div>
                </div>
            )}

            {/* Summary Cards */}
            {!isLoadingAny && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-slide-up">
                    <div className="card p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--danger-soft, #fef2f2)' }}>
                                <FileWarning size={20} style={{ color: 'var(--danger)' }} />
                            </div>
                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-tertiary)' }}>
                                {period.label}
                            </span>
                        </div>
                        <div className="stat-value">{totals.totalReports.toLocaleString()}</div>
                        <div className="stat-label mt-1">Report Snapshots</div>
                    </div>
                    <div className="card p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--success-soft)' }}>
                                <FolderKanban size={20} style={{ color: 'var(--success)' }} />
                            </div>
                        </div>
                        <div className="stat-value">{totals.totalProjects.toLocaleString()}</div>
                        <div className="stat-label mt-1">Projects Affected</div>
                    </div>
                    <div className="card p-5">
                        <div className="flex items-center justify-between mb-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--info-soft)' }}>
                                <Users size={20} style={{ color: 'var(--info)' }} />
                            </div>
                        </div>
                        <div className="stat-value">{totals.totalUsers.toLocaleString()}</div>
                        <div className="stat-label mt-1">Reporting Users</div>
                    </div>
                </div>
            )}

            {/* Diagnostics — always visible so you can verify the data pipeline */}
            {!isLoadingAny && (
                <div className="card p-4 animate-slide-up" style={{ borderLeft: '4px solid var(--info)' }}>
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            Diagnostics
                        </div>
                    </div>
                    <ul className="text-xs space-y-1" style={{ color: 'var(--text-secondary)' }}>
                        <li>Projects loaded: <strong>{diagnostics.totalProjects.toLocaleString()}</strong></li>
                        <li>Projects with any dynamic data: <strong>{diagnostics.projectsWithAnyDynamicData.toLocaleString()}</strong></li>
                        <li>Total dynamic-data entries: <strong>{diagnostics.totalDynamicData.toLocaleString()}</strong></li>
                        <li>Entries of type "Report": <strong>{diagnostics.totalReportType.toLocaleString()}</strong></li>
                        <li>"Unapproved Content" reports (all time): <strong>{diagnostics.totalUnapprovedAnyTime.toLocaleString()}</strong></li>
                        <li>Eligible after period filter ({period.label}): <strong>{eligibleReports.length.toLocaleString()}</strong></li>
                        {diagnostics.distinctReportNames.length > 0 && (
                            <li>
                                Distinct report names found:{' '}
                                <span style={{ color: 'var(--text-tertiary)' }}>
                                    {diagnostics.distinctReportNames.map((n) => `"${n}"`).join(', ')}
                                </span>
                            </li>
                        )}
                    </ul>
                </div>
            )}

            {/* Grouped List */}
            {!isLoadingAny && (
                <div className="card animate-slide-up">
                    <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-default)' }}>
                        <h2 className="section-title" style={{ margin: 0 }}>
                            By {groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}
                        </h2>
                        <button
                            onClick={() => setSortDir((d) => d === 'desc' ? 'asc' : 'desc')}
                            className="text-xs font-medium px-2 py-1 rounded transition-colors"
                            style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}
                            title="Toggle sort direction"
                        >
                            Count {sortDir === 'desc' ? '↓ (most)' : '↑ (least)'}
                        </button>
                    </div>

                    {groups.length === 0 ? (
                        <div className="text-sm text-center py-10" style={{ color: 'var(--text-tertiary)' }}>
                            No unapproved content reports found in the selected period.
                        </div>
                    ) : (
                        <div>
                            {groups.map((group) => {
                                const expanded = expandedGroups.has(group.key);
                                const detail = groupDetails.get(group.key);
                                return (
                                    <div key={group.key} style={{ borderBottom: '1px solid var(--border-default)' }}>
                                        <button
                                            onClick={() => toggleGroup(group.key, group.reports)}
                                            className="w-full flex items-center gap-3 px-6 py-3 transition-colors text-left"
                                            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                                            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                        >
                                            {expanded ? <ChevronUp size={16} style={{ color: 'var(--text-tertiary)' }} /> : <ChevronDown size={16} style={{ color: 'var(--text-tertiary)' }} />}
                                            <div className="flex-1 min-w-0">
                                                {groupBy === 'project' ? (
                                                    <Link
                                                        to={`/projects/${group.key}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="text-sm font-medium truncate hover:underline"
                                                        style={{ color: 'var(--text-primary)', textDecoration: 'none' }}
                                                        title={group.label}
                                                    >
                                                        {group.label}
                                                    </Link>
                                                ) : (
                                                    <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }} title={group.label}>
                                                        {group.label}
                                                    </span>
                                                )}
                                            </div>
                                            <span
                                                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                                style={{ backgroundColor: 'var(--danger-soft, #fef2f2)', color: 'var(--danger)' }}
                                            >
                                                {group.reports.length.toLocaleString()} {group.reports.length === 1 ? 'snapshot' : 'snapshots'}
                                            </span>
                                        </button>
                                        {expanded && (
                                            <div className="px-6 pb-3" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                                                {!detail || detail.loading ? (
                                                    <div className="flex items-center gap-2 py-4 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                                        <Loader size={14} className="animate-spin" />
                                                        Loading detail… {detail ? `${detail.downloaded.toLocaleString()} / ${detail.total.toLocaleString()}` : ''}
                                                    </div>
                                                ) : detail.items.length === 0 ? (
                                                    <div className="text-xs py-4" style={{ color: 'var(--text-tertiary)' }}>
                                                        No detail items in these {detail.total.toLocaleString()} snapshot{detail.total === 1 ? '' : 's'}.
                                                        {detail.failed > 0 && (
                                                            <span style={{ color: 'var(--danger, #dc2626)', marginLeft: 8 }}>
                                                                ({detail.failed.toLocaleString()} download{detail.failed === 1 ? '' : 's'} failed)
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <>
                                                        {detail.failed > 0 && (
                                                            <div className="text-xs py-2" style={{ color: 'var(--danger, #dc2626)' }}>
                                                                {detail.failed.toLocaleString()} of {detail.total.toLocaleString()} snapshots failed to download.
                                                            </div>
                                                        )}
                                                        <table className="w-full text-sm">
                                                            <thead>
                                                                <tr className="text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                                                                    {groupBy !== 'project' && <th className="py-2 pr-4">Project</th>}
                                                                    <th className="py-2 pr-4">Model</th>
                                                                    <th className="py-2 pr-4">Family</th>
                                                                    {groupBy !== 'user' && <th className="py-2 pr-4">Reported By</th>}
                                                                    <th className="py-2">Reported</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {detail.items.map((it) => (
                                                                    <tr key={it.rowId} style={{ borderTop: '1px solid var(--border-default)' }}>
                                                                        {groupBy !== 'project' && (
                                                                            <td className="py-2 pr-4" style={{ color: 'var(--text-secondary)' }}>
                                                                                <Link
                                                                                    to={`/projects/${it.projectId}`}
                                                                                    className="hover:underline"
                                                                                    style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                                                                                    title={it.projectName}
                                                                                >
                                                                                    {it.projectName}
                                                                                </Link>
                                                                            </td>
                                                                        )}
                                                                        <td className="py-2 pr-4" style={{ color: 'var(--text-secondary)' }}>{it.modelName}</td>
                                                                        <td className="py-2 pr-4" style={{ color: 'var(--text-primary)' }}>{it.familyName}</td>
                                                                        {groupBy !== 'user' && (
                                                                            <td className="py-2 pr-4" style={{ color: 'var(--text-secondary)' }}>{it.reportCreatedBy}</td>
                                                                        )}
                                                                        <td className="py-2" style={{ color: 'var(--text-tertiary)' }}>{ticksToDate(it.reportCreatedTime)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* Trending Unapproved Content Report                                  */
/* ------------------------------------------------------------------ */

type TrendingUnapprovedContentReportProps = {
    style?: CSSProperties | undefined;
    onBack: () => void;
};

type TrendingMode = 'latest' | 'history';
type TrendingGroupBy = 'family' | 'user';

type TrendingItem = {
    familyName: string;
    projectId: string;
    projectName: string;
    modelName: string;
    reportId: string;
    reportCreatedTime: string | null;
    reportCreatedBy: string;
};

type CachedPayload = { items: { ModelName?: string; CmsModelName?: string; FamilyName?: string }[] };

const TRENDING_CACHE_PREFIX = 'trending-unapproved-payload:';

const readPayloadCache = (reportId: string): CachedPayload | null => {
    try {
        const raw = localStorage.getItem(TRENDING_CACHE_PREFIX + reportId);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

const writePayloadCache = (reportId: string, payload: CachedPayload) => {
    try {
        localStorage.setItem(TRENDING_CACHE_PREFIX + reportId, JSON.stringify(payload));
    } catch {
        // quota exceeded — silently ignore; we'll just refetch next time
    }
};

// Kept temporarily as a rollback path while the summary-backed report settles.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TrendingUnapprovedContentReport = ({ style, onBack }: TrendingUnapprovedContentReportProps) => {
    const [periodId, setPeriodId] = useState<'week' | 'month' | 'quarter'>('month');
    const [mode, setMode] = useState<TrendingMode>('latest');
    const [groupBy, setGroupBy] = useState<TrendingGroupBy>('family');
    const [generating, setGenerating] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0, fromCache: 0, failed: 0 });
    const [items, setItems] = useState<TrendingItem[] | null>(null);
    const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
    const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
    const cancelRef = React.useRef(false);
    const download = useDownloadAttachment();

    const period = PERIOD_OPTIONS.find((p) => p.id === periodId)!;
    const cutoffSeconds = useMemo(() => {
        const cutoffMs = Date.now() - period.days * 24 * 60 * 60 * 1000;
        return Math.floor(cutoffMs / 1000);
    }, [period]);

    const { data, loading: projectsLoading, refetch } = useQuery(GET_PROJECTS_REPORT_DYNAMIC_DATA);

    // Eligible reports (metadata) by mode
    const reportsToProcess = useMemo(() => {
        const projects: any[] = data?.ProjectList?.nodes || [];
        const all: { projectId: string; projectName: string; report: any }[] = [];
        for (const project of projects) {
            if (!project?.id) continue;
            const matching = (project.dynamicData || []).filter((d: any) =>
                d?.type === 'Report' &&
                (d?.name || '').toLowerCase().trim() === 'unapproved content' &&
                d?.data &&
                d?.createdTime &&
                Number(d.createdTime) >= cutoffSeconds
            );
            for (const report of matching) {
                all.push({ projectId: project.id, projectName: project.name ?? '(unnamed project)', report });
            }
        }
        if (mode === 'history') return all;
        // latest per project
        const latestPerProject = new Map<string, typeof all[number]>();
        for (const entry of all) {
            const current = latestPerProject.get(entry.projectId);
            if (!current || Number(entry.report.createdTime) > Number(current.report.createdTime)) {
                latestPerProject.set(entry.projectId, entry);
            }
        }
        return Array.from(latestPerProject.values());
    }, [data, cutoffSeconds, mode]);

    const generate = useCallback(async () => {
        cancelRef.current = false;
        setGenerating(true);
        setItems(null);
        setExpandedFamilies(new Set());

        // Snapshot the initial set, then keep a mutable array we can refresh
        // mid-run by re-querying ProjectList (pre-signed URLs typically expire
        // after ~15 minutes which is why long runs collapse after ~3000 items).
        let reports = reportsToProcess.slice();
        let done = 0;
        let fromCache = 0;
        let failed = 0;
        setProgress({ done: 0, total: reports.length, fromCache: 0, failed: 0 });

        const CONCURRENCY = 4;
        const MAX_RETRIES = 3;
        // Streak threshold — if this many fetches fail in a row (after retries),
        // we assume the pre-signed URLs have expired and refresh them.
        const STREAK_THRESHOLD = 12;
        const collected: TrendingItem[] = [];

        let consecutiveFailures = 0;
        let refreshing: Promise<void> | null = null;
        let nextIndex = 0;

        const refreshUrls = async () => {
            if (refreshing) return refreshing;
            refreshing = (async () => {
                try {
                    const fresh = await refetch();
                    const freshProjects: any[] = fresh?.data?.ProjectList?.nodes || [];
                    // Build map of reportId -> fresh report metadata
                    const urlMap = new Map<string, any>();
                    for (const project of freshProjects) {
                        for (const d of (project?.dynamicData || [])) {
                            if (d?.id) urlMap.set(d.id, d);
                        }
                    }
                    // Replace report metadata for remaining (not-yet-processed) entries
                    for (let j = nextIndex; j < reports.length; j++) {
                        const freshReport = urlMap.get(reports[j].report.id);
                        if (freshReport) reports[j] = { ...reports[j], report: freshReport };
                    }
                    consecutiveFailures = 0;
                } catch {
                    // ignore; downloads will keep failing and user can cancel
                } finally {
                    refreshing = null;
                }
            })();
            return refreshing;
        };

        const processReport = async (entry: typeof reports[number]) => {
            const { projectId, projectName, report } = entry;
            const reportId: string = report.id;
            let payload = readPayloadCache(reportId);
            if (payload) {
                fromCache++;
                // NOTE: don't reset consecutiveFailures here — cache hits tell us
                // nothing about whether the pre-signed URLs are still valid.
            } else {
                for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
                    if (cancelRef.current) return;
                    try {
                        const raw = await download(report.data);
                        let parsed: any;
                        if (typeof raw === 'string') {
                            try { parsed = JSON.parse(raw); }
                            catch { parsed = JSON.parse(atob(raw)); }
                        } else {
                            parsed = raw;
                        }
                        const list: any[] = Array.isArray(parsed) ? parsed : [parsed];
                        payload = { items: list };
                        writePayloadCache(reportId, payload);
                        consecutiveFailures = 0;
                        break;
                    } catch {
                        if (attempt < MAX_RETRIES) {
                            // exponential backoff: 500ms, 1.5s, 4.5s
                            await new Promise((r) => setTimeout(r, 500 * Math.pow(3, attempt)));
                        }
                    }
                }
                if (!payload) {
                    failed++;
                    consecutiveFailures++;
                    if (consecutiveFailures >= STREAK_THRESHOLD && !cancelRef.current) {
                        // URLs probably expired — refresh and let workers continue
                        await refreshUrls();
                    }
                    return;
                }
            }
            for (const item of payload.items) {
                const familyName = item.FamilyName;
                if (!familyName) continue;
                collected.push({
                    familyName,
                    projectId,
                    projectName,
                    modelName: item.ModelName ?? item.CmsModelName ?? '',
                    reportId,
                    reportCreatedTime: report.createdTime ?? null,
                    reportCreatedBy: report.createdBy?.name || report.createdById || '(unknown user)',
                });
            }
        };

        const worker = async () => {
            while (!cancelRef.current) {
                const i = nextIndex++;
                if (i >= reports.length) return;
                // If a URL refresh is in flight, wait so we use fresh URLs
                if (refreshing) await refreshing;
                await processReport(reports[i]);
                done++;
                setProgress({ done, total: reports.length, fromCache, failed });
            }
        };

        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, reports.length) }, () => worker()));

        if (!cancelRef.current) {
            setItems(collected);
        }
        setGenerating(false);
    }, [download, reportsToProcess, refetch]);

    const cancel = () => {
        cancelRef.current = true;
        setGenerating(false);
    };

    // Reset items when inputs change
    useEffect(() => {
        setItems(null);
        setExpandedFamilies(new Set());
    }, [periodId, mode]);

    // Collapse expanded rows (their keys differ across groupings) when grouping changes
    useEffect(() => {
        setExpandedFamilies(new Set());
    }, [groupBy]);

    // Aggregate items by family or user (exact-match keys, no normalisation)
    const families = useMemo(() => {
        if (!items) return [];
        const map = new Map<string, {
            key: string;
            label: string;
            occurrences: number;
            projects: Set<string>;
            users: Set<string>;
            families: Set<string>;
            firstSeen: number;
            lastSeen: number;
            entries: TrendingItem[];
        }>();
        for (const it of items) {
            const key = groupBy === 'user' ? it.reportCreatedBy : it.familyName;
            let bucket = map.get(key);
            if (!bucket) {
                bucket = {
                    key,
                    label: key,
                    occurrences: 0,
                    projects: new Set(),
                    users: new Set(),
                    families: new Set(),
                    firstSeen: Number.POSITIVE_INFINITY,
                    lastSeen: 0,
                    entries: [],
                };
                map.set(key, bucket);
            }
            bucket.occurrences++;
            bucket.projects.add(it.projectId);
            bucket.users.add(it.reportCreatedBy);
            bucket.families.add(it.familyName);
            bucket.entries.push(it);
            const t = Number(it.reportCreatedTime || 0);
            if (t && t < bucket.firstSeen) bucket.firstSeen = t;
            if (t && t > bucket.lastSeen) bucket.lastSeen = t;
        }
        const arr = Array.from(map.values());
        arr.sort((a, b) => sortDir === 'desc' ? b.occurrences - a.occurrences : a.occurrences - b.occurrences);
        return arr;
    }, [items, sortDir, groupBy]);

    const totals = useMemo(() => {
        if (!items) return { distinctFamilies: 0, totalOccurrences: 0, projects: 0 };
        const projects = new Set(items.map((i) => i.projectId));
        const distinctFamilies = new Set(items.map((i) => i.familyName));
        return { distinctFamilies: distinctFamilies.size, totalOccurrences: items.length, projects: projects.size };
    }, [items]);

    const toggleFamily = (key: string) => {
        setExpandedFamilies((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const clearCache = () => {
        try {
            const keys: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && k.startsWith(TRENDING_CACHE_PREFIX)) keys.push(k);
            }
            for (const k of keys) localStorage.removeItem(k);
        } catch {
            // ignore
        }
    };

    return (
        <div className="page-container" style={{ ...style, overflow: 'auto' }} data-testid="TrendingUnapprovedContentReport">
            {/* Header */}
            <div className="page-header">
                <div>
                    <button
                        onClick={onBack}
                        className="flex items-center gap-1 text-xs font-medium mb-2 transition-colors"
                        style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        onMouseOver={(e) => { e.currentTarget.style.color = 'var(--accent)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
                    >
                        <ArrowLeft size={14} /> Back to Reports
                    </button>
                    <h1 className="page-title">Trending Unapproved Content</h1>
                    <p className="page-subtitle">
                        Family-level frequency analysis across all projects
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>Period:</span>
                        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-default)' }}>
                            {PERIOD_OPTIONS.map((p) => (
                                <button
                                    key={p.id}
                                    onClick={() => setPeriodId(p.id)}
                                    disabled={generating}
                                    className="px-3 py-1.5 text-xs font-medium transition-colors"
                                    style={{
                                        backgroundColor: periodId === p.id ? 'var(--accent)' : 'var(--bg-card)',
                                        color: periodId === p.id ? '#fff' : 'var(--text-secondary)',
                                        border: 'none',
                                        cursor: generating ? 'not-allowed' : 'pointer',
                                        opacity: generating ? 0.6 : 1,
                                        outline: 'none',
                                    }}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>Mode:</span>
                        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-default)' }}>
                            {([
                                { id: 'latest', label: 'Latest per project' },
                                { id: 'history', label: 'Full history' },
                            ] as { id: TrendingMode; label: string }[]).map((m) => (
                                <button
                                    key={m.id}
                                    onClick={() => setMode(m.id)}
                                    disabled={generating}
                                    className="px-3 py-1.5 text-xs font-medium transition-colors"
                                    style={{
                                        backgroundColor: mode === m.id ? 'var(--accent)' : 'var(--bg-card)',
                                        color: mode === m.id ? '#fff' : 'var(--text-secondary)',
                                        border: 'none',
                                        cursor: generating ? 'not-allowed' : 'pointer',
                                        opacity: generating ? 0.6 : 1,
                                        outline: 'none',
                                    }}
                                >
                                    {m.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>Group by:</span>
                        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-default)' }}>
                            {([
                                { id: 'family', label: 'Family' },
                                { id: 'user', label: 'User' },
                            ] as { id: TrendingGroupBy; label: string }[]).map((g) => (
                                <button
                                    key={g.id}
                                    onClick={() => setGroupBy(g.id)}
                                    className="px-3 py-1.5 text-xs font-medium transition-colors"
                                    style={{
                                        backgroundColor: groupBy === g.id ? 'var(--accent)' : 'var(--bg-card)',
                                        color: groupBy === g.id ? '#fff' : 'var(--text-secondary)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        outline: 'none',
                                    }}
                                >
                                    {g.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <RefreshButton onRefresh={refetch} className="btn-secondary" iconSize={16} />
                </div>
            </div>

            {/* Run / Cancel control */}
            <div className="card p-4 animate-slide-up flex items-center justify-between flex-wrap gap-3">
                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {projectsLoading ? (
                        <>Loading project list…</>
                    ) : (
                        <>
                            Ready to process <strong>{reportsToProcess.length.toLocaleString()}</strong> snapshot{reportsToProcess.length === 1 ? '' : 's'}
                            {mode === 'latest' ? ' (latest per project)' : ' (full history — this may take several minutes)'}.
                            {' '}Payloads are cached in your browser, so re-runs only download new snapshots.
                        </>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={clearCache}
                        disabled={generating}
                        className="text-xs font-medium px-3 py-1.5 rounded transition-colors"
                        style={{
                            background: 'var(--bg-secondary)',
                            color: 'var(--text-secondary)',
                            border: 'none',
                            cursor: generating ? 'not-allowed' : 'pointer',
                            opacity: generating ? 0.6 : 1,
                        }}
                        title="Clear local cache of downloaded report payloads"
                    >
                        Clear cache
                    </button>
                    {generating ? (
                        <button
                            onClick={cancel}
                            className="text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors"
                            style={{ background: 'var(--danger)', color: '#fff', border: 'none', cursor: 'pointer' }}
                        >
                            <X size={14} /> Cancel
                        </button>
                    ) : (
                        <button
                            onClick={generate}
                            disabled={projectsLoading || reportsToProcess.length === 0}
                            className="text-xs font-semibold px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors"
                            style={{
                                background: 'var(--accent)',
                                color: '#fff',
                                border: 'none',
                                cursor: (projectsLoading || reportsToProcess.length === 0) ? 'not-allowed' : 'pointer',
                                opacity: (projectsLoading || reportsToProcess.length === 0) ? 0.6 : 1,
                            }}
                        >
                            <Play size={14} /> Generate Report
                        </button>
                    )}
                </div>
            </div>

            {/* Progress */}
            {generating && (
                <div className="card p-4 animate-slide-up">
                    <div className="flex items-center gap-2 mb-2">
                        <Loader size={14} className="animate-spin" style={{ color: 'var(--accent)' }} />
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            Processing snapshots…
                        </div>
                    </div>
                    <div className="text-xs mb-2" style={{ color: 'var(--text-tertiary)' }}>
                        {progress.done.toLocaleString()} / {progress.total.toLocaleString()} processed
                        {' · '}{progress.fromCache.toLocaleString()} from cache
                        {progress.failed > 0 && (
                            <span style={{ color: 'var(--danger, #dc2626)' }}> · {progress.failed.toLocaleString()} failed</span>
                        )}
                    </div>
                    <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                        <div
                            className="h-full transition-all"
                            style={{
                                width: progress.total ? `${(progress.done / progress.total) * 100}%` : '0%',
                                backgroundColor: 'var(--accent)',
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Results */}
            {items && !generating && (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-slide-up">
                        <div className="card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--accent-soft)' }}>
                                    <Sparkles size={20} style={{ color: 'var(--accent)' }} />
                                </div>
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-tertiary)' }}>
                                    {period.label}
                                </span>
                            </div>
                            <div className="stat-value">{totals.distinctFamilies.toLocaleString()}</div>
                            <div className="stat-label mt-1">Distinct Families</div>
                        </div>
                        <div className="card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--danger-soft, #fef2f2)' }}>
                                    <FileWarning size={20} style={{ color: 'var(--danger)' }} />
                                </div>
                            </div>
                            <div className="stat-value">{totals.totalOccurrences.toLocaleString()}</div>
                            <div className="stat-label mt-1">Total Occurrences</div>
                        </div>
                        <div className="card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--success-soft)' }}>
                                    <FolderKanban size={20} style={{ color: 'var(--success)' }} />
                                </div>
                            </div>
                            <div className="stat-value">{totals.projects.toLocaleString()}</div>
                            <div className="stat-label mt-1">Projects Affected</div>
                        </div>
                    </div>

                    <div className="card animate-slide-up">
                        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-default)' }}>
                            <h2 className="section-title" style={{ margin: 0 }}>{groupBy === 'user' ? 'By User' : 'By Family'}</h2>
                            <button
                                onClick={() => setSortDir((d) => d === 'desc' ? 'asc' : 'desc')}
                                className="text-xs font-medium px-2 py-1 rounded transition-colors"
                                style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}
                                title="Toggle sort direction"
                            >
                                Occurrences {sortDir === 'desc' ? '↓ (most)' : '↑ (least)'}
                            </button>
                        </div>

                        {families.length === 0 ? (
                            <div className="text-sm text-center py-10" style={{ color: 'var(--text-tertiary)' }}>
                                No {groupBy === 'user' ? 'users' : 'families'} found in the selected period.
                            </div>
                        ) : (
                            <div>
                                {families.map((fam) => {
                                    const expanded = expandedFamilies.has(fam.key);
                                    const secondaryCount = groupBy === 'user' ? fam.families.size : fam.users.size;
                                    const secondaryLabel = groupBy === 'user'
                                        ? `${secondaryCount.toLocaleString()} famil${secondaryCount === 1 ? 'y' : 'ies'}`
                                        : `${secondaryCount.toLocaleString()} user${secondaryCount === 1 ? '' : 's'}`;
                                    return (
                                        <div key={fam.key} style={{ borderBottom: '1px solid var(--border-default)' }}>
                                            <button
                                                onClick={() => toggleFamily(fam.key)}
                                                className="w-full flex items-center gap-3 px-6 py-3 transition-colors text-left"
                                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                                                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                            >
                                                {expanded ? <ChevronUp size={16} style={{ color: 'var(--text-tertiary)' }} /> : <ChevronDown size={16} style={{ color: 'var(--text-tertiary)' }} />}
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }} title={fam.label}>
                                                        {fam.label}
                                                    </div>
                                                    <div className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                                                        {fam.projects.size.toLocaleString()} project{fam.projects.size === 1 ? '' : 's'}
                                                        {' · '}{secondaryLabel}
                                                        {fam.lastSeen > 0 && <> · last seen {ticksToDate(String(fam.lastSeen))}</>}
                                                    </div>
                                                </div>
                                                <span
                                                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                                    style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }}
                                                >
                                                    {fam.occurrences.toLocaleString()} occurrence{fam.occurrences === 1 ? '' : 's'}
                                                </span>
                                            </button>
                                            {expanded && (
                                                <div className="px-6 pb-3" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                                                    <table className="w-full text-sm">
                                                        <thead>
                                                            <tr className="text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                                                                <th className="py-2 pr-4">Project</th>
                                                                <th className="py-2 pr-4">Model</th>
                                                                <th className="py-2 pr-4">{groupBy === 'user' ? 'Family' : 'Reported By'}</th>
                                                                <th className="py-2">Reported</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {fam.entries.map((it, i) => (
                                                                <tr key={`${it.reportId}-${i}`} style={{ borderTop: '1px solid var(--border-default)' }}>
                                                                    <td className="py-2 pr-4" style={{ color: 'var(--text-secondary)' }}>
                                                                        <Link
                                                                            to={`/projects/${it.projectId}`}
                                                                            className="hover:underline"
                                                                            style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}
                                                                            title={it.projectName}
                                                                        >
                                                                            {it.projectName}
                                                                        </Link>
                                                                    </td>
                                                                    <td className="py-2 pr-4" style={{ color: 'var(--text-secondary)' }}>{it.modelName}</td>
                                                                    <td className="py-2 pr-4" style={{ color: 'var(--text-secondary)' }}>{groupBy === 'user' ? it.familyName : it.reportCreatedBy}</td>
                                                                    <td className="py-2" style={{ color: 'var(--text-tertiary)' }}>{ticksToDate(it.reportCreatedTime)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Found in Library (Enterprise) Report                              */
/* ------------------------------------------------------------------ */



type FoundInLibraryReportProps = {
    style?: CSSProperties | undefined;
    onBack: () => void;
};

const FoundInLibraryReport = ({ style, onBack }: FoundInLibraryReportProps) => {
    const [period, setPeriod] = useState<FILPeriod>('quarter');
    const { generate, cancel, report, phase, progress, error } = useFoundInLibraryReport({ period });

    const loading = phase !== 'idle' && phase !== 'done';
    const periodLabel = FIL_PERIOD_OPTIONS.find((p) => p.id === period)?.label ?? '';

    const phaseLabel: Record<typeof phase, string> = {
        idle: '',
        'fetching-projects': 'Loading projects…',
        'fetching-models': `Scanning models… ${progress.fetchedModels.toLocaleString()} fetched, ${progress.eligibleModels.toLocaleString()} eligible`,
        downloading: `Downloading cache files… ${progress.downloadedModels.toLocaleString()} / ${progress.eligibleModels.toLocaleString()}`,
        done: 'Complete',
    };

    /** Largest familyFoundPct across rows — used to scale the bar chart. */
    const maxPct = useMemo(() => {
        if (!report?.rows.length) return 100;
        return Math.max(...report.rows.map((r) => r.familyFoundPct ?? 0), 1);
    }, [report]);

    return (
        <div className="page-container" style={{ ...style, overflow: 'auto' }} data-testid="FoundInLibraryReport">
            {/* Header */}
            <div className="page-header">
                <div>
                    <button
                        onClick={onBack}
                        className="flex items-center gap-1 text-xs font-medium mb-2 transition-colors"
                        style={{ color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                        onMouseOver={(e) => { e.currentTarget.style.color = 'var(--accent)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
                    >
                        <ArrowLeft size={14} /> Back to Reports
                    </button>
                    <h1 className="page-title">Found in Library</h1>
                    <p className="page-subtitle">
                        Enterprise breakdown of library content match rate by client
                    </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Time range selector */}
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>Period:</span>
                        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-default)' }}>
                            {FIL_PERIOD_OPTIONS.map((p) => (
                                <button
                                    key={p.id}
                                    onClick={() => setPeriod(p.id)}
                                    disabled={loading}
                                    className="px-3 py-1.5 text-xs font-medium transition-colors"
                                    style={{
                                        backgroundColor: period === p.id ? 'var(--accent)' : 'var(--bg-card)',
                                        color: period === p.id ? '#fff' : 'var(--text-secondary)',
                                        border: 'none',
                                        cursor: loading ? 'not-allowed' : 'pointer',
                                        outline: 'none',
                                        opacity: loading ? 0.6 : 1,
                                    }}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Generate / Cancel button */}
                    {loading ? (
                        <button
                            onClick={cancel}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                            style={{ backgroundColor: 'var(--danger-soft, #fef2f2)', color: 'var(--danger)', border: '1px solid var(--danger)', cursor: 'pointer' }}
                        >
                            <X size={13} /> Cancel
                        </button>
                    ) : (
                        <button
                            onClick={generate}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                            style={{ backgroundColor: 'var(--success)', color: '#fff', border: 'none', cursor: 'pointer' }}
                            onMouseOver={(e) => { e.currentTarget.style.opacity = '0.85'; }}
                            onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
                        >
                            <Play size={13} fill="#fff" /> Generate Report
                        </button>
                    )}
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="card p-6 flex items-center gap-3 animate-slide-up" style={{ borderLeft: '4px solid var(--danger)' }}>
                    <AlertCircle size={20} style={{ color: 'var(--danger)' }} />
                    <div>
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Failed to generate report</div>
                        <div className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>{error}</div>
                    </div>
                </div>
            )}

            {/* Loading / Progress */}
            {loading && (
                <div className="card p-8 flex flex-col items-center justify-center gap-3 animate-slide-up">
                    <Loader size={28} className="animate-spin" style={{ color: 'var(--success)' }} />
                    <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {phaseLabel[phase]}
                    </div>
                    {phase === 'downloading' && progress.eligibleModels > 0 && (
                        <div className="w-full max-w-xs">
                            <div
                                className="w-full rounded-full overflow-hidden"
                                style={{ height: 6, backgroundColor: 'var(--bg-secondary)' }}
                            >
                                <div
                                    className="h-full rounded-full transition-all duration-300"
                                    style={{
                                        width: `${Math.round((progress.downloadedModels / progress.eligibleModels) * 100)}%`,
                                        backgroundColor: 'var(--success)',
                                    }}
                                />
                            </div>
                            <div className="text-xs mt-1 text-center" style={{ color: 'var(--text-tertiary)' }}>
                                {progress.downloadedModels.toLocaleString()} of {progress.eligibleModels.toLocaleString()} models
                                {progress.fromCache > 0 && (
                                    <span style={{ color: 'var(--success)', marginLeft: 6 }}>
                                        ({progress.fromCache.toLocaleString()} from cache)
                                    </span>
                                )}
                                {progress.failedModels > 0 && (
                                    <span style={{ color: 'var(--danger)', marginLeft: 6 }}>
                                        ({progress.failedModels.toLocaleString()} failed)
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Idle placeholder */}
            {phase === 'idle' && !report && !error && (
                <div className="card p-10 flex flex-col items-center gap-3 animate-slide-up">
                    <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center"
                        style={{ backgroundColor: 'var(--success-soft)' }}
                    >
                        <Library size={28} style={{ color: 'var(--success)' }} />
                    </div>
                    <div className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Ready to generate</div>
                    <div className="text-sm text-center max-w-sm" style={{ color: 'var(--text-tertiary)' }}>
                        This report downloads cache files for every model updated in the selected period.
                        Choose a time range above and click <strong>Generate Report</strong>.
                        Results are cached locally — subsequent runs for the same period will be significantly faster.
                    </div>
                </div>
            )}

            {/* Results */}
            {report && !loading && (
                <>
                    {/* Summary cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-slide-up">
                        {/* Enterprise family % */}
                        <div className="card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--success-soft)' }}>
                                    <CheckCircle2 size={20} style={{ color: 'var(--success)' }} />
                                </div>
                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-tertiary)' }}>
                                    {periodLabel}
                                </span>
                            </div>
                            <div className="stat-value">
                                {report.enterpriseFamilyFoundPct !== null ? `${report.enterpriseFamilyFoundPct}%` : '—'}
                            </div>
                            <div className="stat-label mt-1">Families Found (enterprise)</div>
                        </div>

                        {/* Models analyzed */}
                        <div className="card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--accent-soft)' }}>
                                    <Box size={20} style={{ color: 'var(--accent)' }} />
                                </div>
                            </div>
                            <div className="stat-value">{report.totalModelsAnalyzed.toLocaleString()}</div>
                            <div className="stat-label mt-1">Models Analyzed</div>
                            {report.totalModelsSkipped > 0 && (
                                <div className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                                    {report.totalModelsSkipped.toLocaleString()} skipped (no health data)
                                </div>
                            )}
                        </div>

                        {/* Clients */}
                        <div className="card p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--warning-soft)' }}>
                                    <Users size={20} style={{ color: 'var(--warning)' }} />
                                </div>
                            </div>
                            <div className="stat-value">{report.rows.length.toLocaleString()}</div>
                            <div className="stat-label mt-1">Clients</div>
                        </div>
                    </div>

                    {/* Bar chart — % families found by client */}
                    <div className="card p-6 animate-slide-up">
                        <div className="flex items-center gap-2 mb-5">
                            <BarChart3 size={18} style={{ color: 'var(--text-tertiary)' }} />
                            <h2 className="section-title" style={{ margin: 0 }}>% Families Found in Library — by Client</h2>
                        </div>
                        {report.rows.length === 0 ? (
                            <div className="text-sm text-center py-8" style={{ color: 'var(--text-tertiary)' }}>
                                No model health data found in the selected period.
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {report.rows.map((row) => {
                                    const pct = row.familyFoundPct ?? 0;
                                    const barPct = (pct / maxPct) * 100;
                                    const barColor = pct >= 80 ? 'var(--success)' : pct >= 50 ? 'var(--warning)' : 'var(--danger)';
                                    return (
                                        <div key={row.clientId}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span
                                                    className="text-sm font-medium truncate"
                                                    style={{ color: 'var(--text-primary)' }}
                                                    title={row.clientName}
                                                >
                                                    {row.clientName}
                                                </span>
                                                <div className="flex items-center gap-4 text-xs flex-shrink-0 ml-4">
                                                    <span style={{ color: barColor, fontWeight: 600 }}>
                                                        {row.familyFoundPct !== null ? `${row.familyFoundPct}%` : '—'}
                                                    </span>
                                                    <span style={{ color: 'var(--text-tertiary)' }}>
                                                        {row.modelCount.toLocaleString()} model{row.modelCount !== 1 ? 's' : ''}
                                                    </span>
                                                </div>
                                            </div>
                                            <div
                                                className="w-full h-5 rounded-md overflow-hidden"
                                                style={{ backgroundColor: 'var(--bg-secondary)' }}
                                            >
                                                <div
                                                    className="h-full rounded-md transition-all duration-500 ease-out"
                                                    style={{
                                                        width: `${barPct}%`,
                                                        backgroundColor: barColor,
                                                        minWidth: barPct > 0 ? '4px' : '0',
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Detail table */}
                    <div className="card animate-slide-up">
                        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--border-default)' }}>
                            <h2 className="section-title" style={{ margin: 0 }}>Detailed Breakdown by Client</h2>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr
                                        className="text-left text-xs font-semibold uppercase tracking-wider"
                                        style={{ color: 'var(--text-tertiary)', backgroundColor: 'var(--bg-secondary)' }}
                                    >
                                        <th className="px-6 py-3">Client</th>
                                        <th className="px-6 py-3 text-right">Models</th>
                                        <th className="px-6 py-3 text-right">Families Found</th>
                                        <th className="px-6 py-3 text-right">Total Families</th>
                                        <th className="px-6 py-3 text-right">% Families Found</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.rows.map((row, idx) => {
                                        const famColor = row.familyFoundPct === null ? 'var(--text-tertiary)'
                                            : row.familyFoundPct >= 80 ? 'var(--success)'
                                            : row.familyFoundPct >= 50 ? 'var(--warning)'
                                            : 'var(--danger)';
                                        return (
                                            <tr
                                                key={row.clientId}
                                                className="transition-colors"
                                                style={{
                                                    borderBottom: '1px solid var(--border-default)',
                                                    backgroundColor: idx % 2 === 0 ? 'transparent' : 'var(--bg-secondary)',
                                                }}
                                                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                                                onMouseOut={(e) => { e.currentTarget.style.backgroundColor = idx % 2 === 0 ? 'transparent' : 'var(--bg-secondary)'; }}
                                            >
                                                <td className="px-6 py-3 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                                    {row.clientName}
                                                </td>
                                                <td className="px-6 py-3 text-sm text-right tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                                                    {row.modelCount.toLocaleString()}
                                                </td>
                                                <td className="px-6 py-3 text-sm text-right tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                                                    {row.foundFamilies.toLocaleString()}
                                                </td>
                                                <td className="px-6 py-3 text-sm text-right tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                                                    {row.totalFamilies.toLocaleString()}
                                                </td>
                                                <td className="px-6 py-3 text-sm text-right tabular-nums font-semibold" style={{ color: famColor }}>
                                                    {row.familyFoundPct !== null ? `${row.familyFoundPct}%` : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                {report.rows.length > 1 && (
                                    <tfoot>
                                        <tr
                                            className="font-semibold text-sm"
                                            style={{ borderTop: '2px solid var(--border-default)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
                                        >
                                            <td className="px-6 py-3">Enterprise Total</td>
                                            <td className="px-6 py-3 text-right tabular-nums">{report.totalModelsAnalyzed.toLocaleString()}</td>
                                            <td className="px-6 py-3 text-right tabular-nums">
                                                {report.rows.reduce((s, r) => s + r.foundFamilies, 0).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-3 text-right tabular-nums">
                                                {report.rows.reduce((s, r) => s + r.totalFamilies, 0).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-3 text-right tabular-nums" style={{ color: 'var(--success)' }}>
                                                {report.enterpriseFamilyFoundPct !== null ? `${report.enterpriseFamilyFoundPct}%` : '—'}
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Reports;
